// @ts-nocheck
// ═══ SECTIONS ═════════════════════════════════════════════
// Sections panel ported verbatim from legacy/EMR Forms.html: add/rename/delete
// a section, drag-and-drop reordering (grab-from-handle), per-section question
// checklist (with the "a question can live in only one section" filter),
// up/down move arrows, and the per-section "Then go to" dropdown
// (populateSecNextSel, with cross-section "already used" annotation/disable).

import type { Ctx } from './engineTypes.ts';
import { esc } from './state.ts';

export function attachSections(ctx: Ctx): void {
  const doc = ctx.container;
  const state = ctx.state;
  const $ = (id: string): any => doc.querySelector('#' + id);
  const syncJson = () => ctx.syncJson();
  const rebuildGotos = () => ctx.rebuildGotos();

  // ── Add Section ──
  $('addSection').onclick = () => {
    state.sections.push({ id: 'sec_' + (state.sections.length + 1), title: 'New Section', questionIds: [], defaultNext: '' });
    renderSections(); syncJson(); rebuildGotos();
  };

  let _secDragSrc: number | null = null;

  function renderSections() {
    const wrap = $('sectionsWrap');
    const scrollHost = wrap.closest('.card');
    const savedScroll = scrollHost ? scrollHost.scrollTop : 0;
    const listScrolls = new Map<string, number>();
    wrap.querySelectorAll('.sec-card').forEach((card: any, i: number) => {
      const sec = state.sections[i];
      const list = card.querySelector('.sec-q-list');
      if (sec && list) listScrolls.set(sec.id, list.scrollTop);
    });
    wrap.innerHTML = '';
    if (!state.sections.length) { wrap.innerHTML = '<p class="txt-xs muted2" style="text-align:center;padding:8px 0">No sections yet. Click "+ Add" to create one.</p>'; return; }
    state.sections.forEach((s: any, idx: number) => {
      const box = document.createElement('div'); box.className = 'sec-card';
      const titleRow = document.createElement('div'); titleRow.style.cssText = 'display:flex;gap:6px;margin-bottom:9px;align-items:center';
      const secNum = document.createElement('div'); secNum.style.cssText = 'min-width:22px;height:22px;border-radius:6px;background:var(--brand-dim);color:var(--brand);font-size:11px;font-weight:700;display:flex;align-items:center;justify-content:center;flex-shrink:0'; secNum.textContent = String(idx + 1);
      const dragHandle = document.createElement('div'); dragHandle.className = 'sec-drag-handle'; dragHandle.title = 'Drag to reorder'; dragHandle.innerHTML = '⠿';
      const titleInp = document.createElement('input'); titleInp.type = 'text'; titleInp.value = esc(s.title); titleInp.placeholder = 'Section title'; titleInp.style.cssText = 'flex:1;font-size:13px;font-weight:600';
      titleRow.append(secNum, dragHandle, titleInp); box.appendChild(titleRow);
      box.draggable = false;
      dragHandle.addEventListener('mousedown', () => { box.draggable = true; });
      box.addEventListener('dragend', () => {
        box.draggable = false; box.classList.remove('sec-dragging');
        wrap.querySelectorAll('.sec-card').forEach((c: any) => c.classList.remove('sec-drag-over-top', 'sec-drag-over-bot'));
        _secDragSrc = null;
      });
      box.addEventListener('dragstart', (e: any) => {
        _secDragSrc = idx; e.dataTransfer.effectAllowed = 'move';
        setTimeout(() => box.classList.add('sec-dragging'), 0);
      });
      box.addEventListener('dragover', (e: any) => {
        e.preventDefault(); if (_secDragSrc === null || _secDragSrc === idx) return;
        const r = box.getBoundingClientRect(); const mid = r.top + r.height / 2;
        box.classList.toggle('sec-drag-over-top', e.clientY < mid);
        box.classList.toggle('sec-drag-over-bot', e.clientY >= mid);
      });
      box.addEventListener('dragleave', () => box.classList.remove('sec-drag-over-top', 'sec-drag-over-bot'));
      box.addEventListener('drop', (e: any) => {
        e.preventDefault(); box.classList.remove('sec-drag-over-top', 'sec-drag-over-bot');
        if (_secDragSrc === null || _secDragSrc === idx) return;
        const r = box.getBoundingClientRect(); const mid = r.top + r.height / 2;
        const insertAfter = e.clientY >= mid;
        const [moved] = state.sections.splice(_secDragSrc, 1);
        let target = idx; if (_secDragSrc < idx) target--;
        state.sections.splice(insertAfter ? target + 1 : target, 0, moved);
        renderSections(); syncJson(); rebuildGotos();
      });
      // ── Questions checklist (exclude questions used in OTHER sections) ──
      const usedElsewhere = new Set<string>();
      state.sections.forEach((os: any, oi: number) => { if (oi !== idx) (os.questionIds || []).forEach((qid: string) => usedElsewhere.add(qid)); });
      const selSet = new Set<string>(s.questionIds || []);
      const qLbl = document.createElement('div'); qLbl.className = 'q-field-lbl'; qLbl.style.marginBottom = '4px'; qLbl.textContent = 'Questions';
      const qList = document.createElement('div'); qList.className = 'sec-q-list';
      const availableQs = state.questions.filter((q: any) => !usedElsewhere.has(q.id) || selSet.has(q.id));
      if (!availableQs.length) { qList.innerHTML = '<p class="txt-xs muted2" style="padding:4px 6px">No questions available.</p>'; }
      availableQs.forEach((q: any) => {
        const item = document.createElement('label'); item.className = 'sec-q-item';
        const cb = document.createElement('input'); cb.type = 'checkbox'; cb.value = q.id; cb.checked = selSet.has(q.id);
        const idTag = document.createElement('span'); idTag.className = 'sec-q-id'; idTag.textContent = q.id; idTag.title = 'Question ID: ' + q.id;
        const nameSpan = document.createElement('span'); nameSpan.textContent = q.label;
        item.append(cb, idTag, nameSpan);
        cb.addEventListener('change', () => {
          if (cb.checked) { if (!s.questionIds.includes(q.id)) s.questionIds.push(q.id); }
          else { s.questionIds = s.questionIds.filter((id: string) => id !== q.id); }
          syncJson(); renderSections();
        });
        qList.appendChild(item);
      });
      box.append(qLbl, qList);
      // ── Then go to ──
      const nextRow = document.createElement('div'); nextRow.style.cssText = 'display:flex;align-items:center;gap:6px;margin-top:9px';
      const nextLbl = document.createElement('span'); nextLbl.className = 'txt-xs muted2'; nextLbl.style.whiteSpace = 'nowrap'; nextLbl.textContent = 'Then go to:';
      const nextSel = document.createElement('select'); nextSel.setAttribute('data-sec-next', '1'); nextSel.dataset.ownerSid = s.id; nextSel.style.cssText = 'flex:1;font-size:12px';
      nextRow.append(nextLbl, nextSel); box.appendChild(nextRow);
      populateSecNextSel(nextSel, s.defaultNext);
      // ── Action row ──
      const actRow = document.createElement('div'); actRow.style.cssText = 'display:flex;align-items:center;justify-content:flex-end;gap:6px;margin-top:9px';
      const muBtn = document.createElement('button'); muBtn.type = 'button'; muBtn.className = 'sec-arrow-btn'; muBtn.title = 'Move up'; muBtn.innerHTML = '&#8679;';
      const mdBtn = document.createElement('button'); mdBtn.type = 'button'; mdBtn.className = 'sec-arrow-btn'; mdBtn.title = 'Move down'; mdBtn.innerHTML = '&#8681;';
      const delBtn = document.createElement('button'); delBtn.type = 'button'; delBtn.className = 'btn sm danger'; delBtn.textContent = 'Delete';
      actRow.append(muBtn, mdBtn, delBtn); box.appendChild(actRow);
      // ── Events ──
      titleInp.addEventListener('input', () => { s.title = titleInp.value; syncJson(); rebuildGotos(); });
      nextSel.addEventListener('change', () => {
        s.defaultNext = nextSel.value; syncJson();
        rebuildGotos();
      });
      muBtn.onclick = () => { if (idx <= 0) return; const [m] = state.sections.splice(idx, 1); state.sections.splice(idx - 1, 0, m); renderSections(); syncJson(); rebuildGotos(); };
      mdBtn.onclick = () => { if (idx >= state.sections.length - 1) return; const [m] = state.sections.splice(idx, 1); state.sections.splice(idx + 1, 0, m); renderSections(); syncJson(); rebuildGotos(); };
      delBtn.onclick = () => { state.sections.splice(idx, 1); state.questions.forEach((q: any) => { const br = q.meta?.branch; if (!br) return; if (br.defaultNext === '@' + s.id) br.defaultNext = ''; Object.keys(br.byValue || {}).forEach(k => { if (br.byValue[k] === '@' + s.id) br.byValue[k] = ''; }); }); renderSections(); syncJson(); rebuildGotos(); };
      wrap.appendChild(box);
      if (listScrolls.has(s.id)) qList.scrollTop = listScrolls.get(s.id);
    });
    if (scrollHost) scrollHost.scrollTop = savedScroll;
  }

  function populateSecNextSel(sel: any, selected?: any) {
    const prev = sel.value;
    const ownerSid = sel.dataset.ownerSid || null;
    const usedBy = new Map<string, string>();
    state.sections.forEach((s: any) => {
      if (s.id === ownerSid) return;
      const dn = s.defaultNext;
      if (typeof dn === 'string' && dn.startsWith('@')) {
        const targetSid = dn.slice(1);
        if (!usedBy.has(targetSid)) usedBy.set(targetSid, s.title || 'Untitled section');
      }
    });
    sel.innerHTML = '';
    const curSel = (selected !== undefined ? selected : prev);
    const opts = [
      { value: '', label: '(Next section in order)' },
      ...state.sections.map((s: any) => {
        const usedByTitle = usedBy.get(s.id) || '';
        return {
          value: '@' + s.id,
          label: '→ ' + s.title,
          usedBy: usedByTitle,
          disabled: !!usedByTitle && curSel !== '@' + s.id,
        };
      }),
      { value: 'end', label: 'End of form' },
    ];
    opts.forEach((o: any) => {
      const opt = document.createElement('option');
      opt.value = o.value;
      opt.textContent = o.label + (o.usedBy ? ' (Used in ' + o.usedBy + ')' : '');
      if (o.disabled) opt.disabled = true;
      if (o.value === curSel) opt.selected = true;
      sel.appendChild(opt);
    });
  }

  ctx.renderSections = renderSections;
  ctx.populateSecNextSel = populateSecNextSel;
}
